/**
 * Types for the case workbench (`/workbench/{reportId}/…` in vNext-api,
 * `docs/workbench.md`): the header, the brief, the case state its views
 * read — the checklist, the files, the evidence, the asks, the review
 * issues, the documents — the timeline, setup, and what a person sends
 * back. Amounts arrive as decimal strings.
 */

import type { CaseActivityEvent } from './case';
import type { TriageStage } from './report-request';

/** A case's workbench mode (vNext-api `WorkbenchMode`): the workbench
 * shows `live` cases; `off` is today's flow alone. */
export type WorkbenchMode = 'off' | 'live';

export type WorkbenchView =
  | 'brief'
  | 'setup'
  | 'needs_you'
  | 'evidence'
  | 'document'
  | 'files'
  | 'chat'
  | 'activity';

export const WORKBENCH_VIEWS: readonly WorkbenchView[] = [
  'brief',
  'setup',
  'needs_you',
  'evidence',
  'document',
  'files',
  'chat',
  'activity',
];

function textOf(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function countOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

// ---------------------------------------------------------------------------
// Needs you's sections (vNext-api `vnext/agents/sections.py`)
// ---------------------------------------------------------------------------

/** Where an ask sits in Needs you, by what it's about — in the order the
 * sections are worked. */
export type Section = 'setup' | 'identity' | 'searches' | 'partner_work' | 'other' | 'draft_review';

export const SECTIONS: readonly Section[] = [
  'setup',
  'identity',
  'searches',
  'partner_work',
  'other',
  'draft_review',
];

export const SECTION_NAMES: Record<Section, string> = {
  setup: 'Setup',
  identity: 'Identity',
  searches: 'Manual searches',
  partner_work: 'Partner work',
  other: 'Other',
  draft_review: 'Draft review',
};

/** A section's things, counted: "1 identity question", "13 manual
 * searches" (the queue row's words). */
const SECTION_WORDS: Record<Section, [string, string]> = {
  setup: ['setup question', 'setup questions'],
  identity: ['identity question', 'identity questions'],
  searches: ['manual search', 'manual searches'],
  partner_work: ['partner task', 'partner tasks'],
  other: ['question', 'questions'],
  draft_review: ['review issue', 'review issues'],
};

export function sectionCount(section: Section, count: number): string {
  const [one, many] = SECTION_WORDS[section];
  return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

export function isSection(value: unknown): value is Section {
  return typeof value === 'string' && (SECTIONS as readonly string[]).includes(value);
}

/** Counts by section, as the API sends them: unknown sections and anything
 * not a count left out. */
export function sectionCounts(raw: unknown): Partial<Record<Section, number>> {
  const counts: Partial<Record<Section, number>> = {};
  if (raw && typeof raw === 'object') {
    for (const [key, n] of Object.entries(raw as Record<string, unknown>)) {
      if (isSection(key) && typeof n === 'number' && n > 0) {
        counts[key] = n;
      }
    }
  }
  return counts;
}

/** What needs a person on the case, counted — the Needs you tab, its rail,
 * and the queue row read the same summary. */
export interface NeedsYouSummary {
  total: number;
  bySection: Partial<Record<Section, number>>;
  /** Asks a task waits on. */
  blocking: number;
  /** The live draft's open errors release refuses on; null where the case
   * isn't live. */
  releaseWaitsOn: string[] | null;
  /** The live Word draft whose open issues Needs you lists. */
  draftId: string | null;
}

interface ApiNeedsYou {
  total?: number;
  by_section?: Record<string, number>;
  blocking?: number;
  release_waits_on?: string[] | null;
  draft_id?: string | null;
}

function needsYouFrom(api: ApiNeedsYou | null | undefined): NeedsYouSummary {
  return {
    total: api?.total ?? 0,
    bySection: sectionCounts(api?.by_section),
    blocking: api?.blocking ?? 0,
    releaseWaitsOn: api?.release_waits_on ?? null,
    draftId: api?.draft_id ?? null,
  };
}

// ---------------------------------------------------------------------------
// Money, spend and the threshold
// ---------------------------------------------------------------------------

export interface Money {
  amount: string;
  currency: string | null;
}

/** The budget threshold the case runs under now — before Start, the budget
 * before Start; after, the case budget — and where its model spend stands
 * against it. A threshold a person raises, never a wall. */
export interface WorkbenchThreshold {
  scope: 'case' | 'before_start';
  /** Null: nothing sets one. */
  usd: string | null;
  /** What the threshold counts: the agents' tasks and the Assistant's replies. */
  spent: string;
  /** Kept for the conversation: a run doesn't start past threshold − reserve. */
  reserve: string;
  reached: boolean;
  runAllowed: boolean;
}

export interface WorkbenchSpend {
  modelsUsd: string;
  npFees: Money[];
  threshold: WorkbenchThreshold;
}

export interface ApiWorkbenchSpend {
  models_usd: string;
  np_fees: { currency: string | null; amount: string }[];
  threshold: {
    scope: 'case' | 'before_start';
    usd: string | null;
    spent: string;
    reserve: string;
    reached: boolean;
    run_allowed: boolean;
  };
}

function thresholdFrom(api: ApiWorkbenchSpend['threshold']): WorkbenchThreshold {
  return {
    scope: api.scope,
    usd: api.usd ?? null,
    spent: api.spent,
    reserve: api.reserve,
    reached: api.reached,
    runAllowed: api.run_allowed,
  };
}

export function transformToWorkbenchSpend(api: ApiWorkbenchSpend): WorkbenchSpend {
  return {
    modelsUsd: api.models_usd,
    npFees: api.np_fees,
    threshold: thresholdFrom(api.threshold),
  };
}

/** The case agent's run: the one queued or running, if any, and when the
 * last statement was recorded. */
export interface WorkbenchRun {
  taskId: string | null;
  status: 'queued' | 'running' | null;
  reason: string | null;
  lastStatementAt: string | null;
}

export interface ApiWorkbenchRun {
  task_id?: string | null;
  status?: string | null;
  reason?: string | null;
  last_statement_at?: string | null;
}

function runFrom(api: ApiWorkbenchRun | null | undefined): WorkbenchRun {
  const status = api?.status;
  return {
    taskId: api?.task_id ?? null,
    status: status === 'queued' || status === 'running' ? status : null,
    reason: api?.reason ?? null,
    lastStatementAt: api?.last_statement_at ?? null,
  };
}

/** Continue pressed: the run queued, or the one already queued. */
export interface RunQueued {
  taskId: string;
  status: string;
}

// ---------------------------------------------------------------------------
// The header
// ---------------------------------------------------------------------------

/** The client playbook version the case is pinned to, and the report type
 * it runs (one of the manifest's codes). */
export interface WorkbenchPlaybook {
  key: string;
  name: string;
  version: number;
  versionId: string;
  reportType: string | null;
  /** The codes the manifest offers for the case's config. */
  offeredReportTypes: string[];
}

/** Where the case's setup stands: the request it's set up from. */
export interface WorkbenchSetupStatus {
  reportRequestId: string;
  triageStage: TriageStage | string;
  /** The request has started: setup is over. */
  started: boolean;
  /** Moves when the request or the case's partner assignments change. */
  version: string;
}

/** The case an evaluation copy was made from. */
export interface WorkbenchCopyOf {
  reportId: string;
  codename: string | null;
  familyKey: string | null;
}

export interface ApiWorkbenchCopyOf {
  report_id: string;
  codename?: string | null;
  family_key?: string | null;
}

export function copyOfFrom(api: ApiWorkbenchCopyOf | null | undefined): WorkbenchCopyOf | null {
  return api
    ? { reportId: api.report_id, codename: api.codename ?? null, familyKey: api.family_key ?? null }
    : null;
}

/** The case page, on the case's own item. */
export function casePathOf(familyKey: string | null, reportId: string): string | null {
  return familyKey ? `/cases/${encodeURIComponent(familyKey)}?item=rep:${reportId}` : null;
}

export interface WorkbenchHeader {
  reportId: string;
  codename: string | null;
  familyKey: string | null;
  mode: WorkbenchMode;
  lifecycleStage: string;
  startedAt: string | null;
  /** An evaluation copy: the case it was made from. */
  evaluationOf: WorkbenchCopyOf | null;
  /** Null for a report with no request (self-served, a batch): no setup. */
  setup: WorkbenchSetupStatus | null;
  playbook: WorkbenchPlaybook | null;
  price: Money | null;
  due: {
    dueAt: string | null;
    turnaroundDays: number | null;
    overridden: boolean;
    expedited: boolean;
  };
  spend: WorkbenchSpend;
  document: {
    isDocx: boolean;
    latestVersion: number | null;
    /** The NP submission whose paragraphs the latest version carries: its
     * byte copy, or — back through direct text edits, which keep every
     * paragraph's id — the one the version it was edited from carries. */
    paragraphSourceReportDataId: string | null;
    approvalRequired: 'disabled' | 'enabled' | string;
    approvalStatus: 'pending' | 'approved' | 'revoked' | null;
  };
  subjects: { id: string; name: string; type: string | null }[];
  counts: {
    checklist: number;
    /** Done, not possible or waived. */
    checklistSettled: number;
    evidence: number;
    files: number;
    filesEntered: number;
    /** Conversations whose last message is the other side's. */
    conversationsAwaiting: number;
  };
  needsYou: NeedsYouSummary;
  run: WorkbenchRun;
  version: string;
}

export interface ApiWorkbenchHeader {
  report_id: string;
  codename: string | null;
  family_key: string | null;
  mode: WorkbenchMode;
  lifecycle_stage: string;
  started_at: string | null;
  evaluation_of?: ApiWorkbenchCopyOf | null;
  setup?: {
    report_request_id: string;
    triage_stage: string;
    started: boolean;
    version: string;
  } | null;
  playbook: {
    key: string;
    name: string;
    version: number;
    version_id: string;
    report_type?: string | null;
    offered_report_types?: string[];
  } | null;
  price: { amount: string; currency: string | null } | null;
  due: {
    due_at: string | null;
    turnaround_days: number | null;
    overridden: boolean;
    expedited: boolean;
  };
  spend: ApiWorkbenchSpend;
  document: {
    is_docx: boolean;
    latest_version: number | null;
    paragraph_source_report_data_id: string | null;
    approval_required: string;
    approval_status: 'pending' | 'approved' | 'revoked' | null;
  };
  subjects: { id: string; name: string; type: string | null }[];
  counts: {
    checklist: number;
    checklist_settled: number;
    evidence: number;
    files: number;
    files_entered: number;
    conversations_awaiting?: number;
  };
  needs_you?: ApiNeedsYou;
  run?: ApiWorkbenchRun;
  version: string;
}

export function transformToWorkbenchHeader(api: ApiWorkbenchHeader): WorkbenchHeader {
  return {
    reportId: api.report_id,
    codename: api.codename,
    familyKey: api.family_key,
    mode: api.mode,
    lifecycleStage: api.lifecycle_stage,
    startedAt: api.started_at,
    evaluationOf: copyOfFrom(api.evaluation_of),
    setup: api.setup
      ? {
          reportRequestId: api.setup.report_request_id,
          triageStage: api.setup.triage_stage,
          started: api.setup.started,
          version: api.setup.version,
        }
      : null,
    playbook: api.playbook
      ? {
          key: api.playbook.key,
          name: api.playbook.name,
          version: api.playbook.version,
          versionId: api.playbook.version_id,
          reportType: api.playbook.report_type ?? null,
          offeredReportTypes: api.playbook.offered_report_types ?? [],
        }
      : null,
    price: api.price,
    due: {
      dueAt: api.due.due_at,
      turnaroundDays: api.due.turnaround_days,
      overridden: api.due.overridden,
      expedited: api.due.expedited,
    },
    spend: transformToWorkbenchSpend(api.spend),
    document: {
      isDocx: api.document.is_docx,
      latestVersion: api.document.latest_version,
      paragraphSourceReportDataId: api.document.paragraph_source_report_data_id,
      approvalRequired: api.document.approval_required,
      approvalStatus: api.document.approval_status,
    },
    subjects: api.subjects,
    counts: {
      checklist: api.counts.checklist,
      checklistSettled: api.counts.checklist_settled,
      evidence: api.counts.evidence,
      files: api.counts.files,
      filesEntered: api.counts.files_entered,
      conversationsAwaiting: api.counts.conversations_awaiting ?? 0,
    },
    needsYou: needsYouFrom(api.needs_you),
    run: runFrom(api.run),
    version: api.version,
  };
}

// ---------------------------------------------------------------------------
// The case list
// ---------------------------------------------------------------------------

/** Where a case stands in the Workbench. */
export type WorkbenchCaseStage = 'setup' | 'started' | 'in_qa' | 'completed' | 'handed_back';

/** One case of `GET /workbench/cases`: every live case, and the ones released
 * to QA, completed or handed back lately (`done`). */
export interface WorkbenchCaseRow {
  reportId: string;
  reportRequestId: string | null;
  codename: string | null;
  familyKey: string | null;
  teamId: string;
  mode: WorkbenchMode;
  lifecycleStage: string;
  stage: WorkbenchCaseStage;
  /** Since when it has stood there. */
  stageAt: string | null;
  /** The Workbench's work on it is over; kept on the list a while, flagged. */
  done: boolean;
  createdAt: string;
  startedAt: string | null;
  playbook: {
    key: string;
    name: string;
    version: number;
    versionId: string;
    reportType: string | null;
  } | null;
  due: WorkbenchHeader['due'];
  /** Open asks, and the live draft's open review issues. */
  needsYou: number;
  /** Asks a task waits on. */
  blocking: number;
  run: WorkbenchRun;
  threshold: WorkbenchThreshold;
  /** An evaluation copy: the case it was made from. */
  copyOf: WorkbenchCopyOf | null;
}

export interface WorkbenchCaseList {
  items: WorkbenchCaseRow[];
  /** How many rows the query matches in all. */
  total: number;
  page: number;
  pageSize: number;
  version: string;
}

/** What the list sorts by; none for the worked order. */
export type WorkbenchCaseSort =
  | 'codename'
  | 'team'
  | 'stage'
  | 'due'
  | 'needs_you'
  | 'run'
  | 'spend'
  | 'started'
  | 'created';

export interface WorkbenchCaseQuery {
  page?: number;
  pageSize?: number;
  /** Over the codename, family, playbook and report type. */
  q?: string;
  stages?: WorkbenchCaseStage[];
  /** True: the finished cases only; false: the open ones only. */
  done?: boolean;
  teamId?: string;
  sort?: WorkbenchCaseSort;
  order?: 'asc' | 'desc';
}

export interface ApiWorkbenchCaseRow {
  report_id: string;
  report_request_id?: string | null;
  codename?: string | null;
  family_key?: string | null;
  team_id: string;
  mode: WorkbenchMode;
  lifecycle_stage: string;
  stage: WorkbenchCaseStage;
  stage_at?: string | null;
  done?: boolean;
  created_at: string;
  started_at?: string | null;
  playbook?: {
    key: string;
    name: string;
    version: number;
    version_id: string;
    report_type?: string | null;
  } | null;
  due: ApiWorkbenchHeader['due'];
  needs_you?: number;
  blocking?: number;
  run?: ApiWorkbenchRun;
  threshold: ApiWorkbenchSpend['threshold'];
  copy_of?: ApiWorkbenchCopyOf | null;
}

export interface ApiWorkbenchCaseList {
  items: ApiWorkbenchCaseRow[];
  total?: number;
  page?: number;
  page_size?: number;
  version: string;
}

export function transformToWorkbenchCaseList(api: ApiWorkbenchCaseList): WorkbenchCaseList {
  return {
    items: (api.items ?? []).map((row) => ({
      reportId: row.report_id,
      reportRequestId: row.report_request_id ?? null,
      codename: row.codename ?? null,
      familyKey: row.family_key ?? null,
      teamId: row.team_id,
      mode: row.mode,
      lifecycleStage: row.lifecycle_stage,
      stage: row.stage,
      stageAt: row.stage_at ?? null,
      done: row.done ?? false,
      createdAt: row.created_at,
      startedAt: row.started_at ?? null,
      playbook: row.playbook
        ? {
            key: row.playbook.key,
            name: row.playbook.name,
            version: row.playbook.version,
            versionId: row.playbook.version_id,
            reportType: row.playbook.report_type ?? null,
          }
        : null,
      due: {
        dueAt: row.due.due_at,
        turnaroundDays: row.due.turnaround_days,
        overridden: row.due.overridden,
        expedited: row.due.expedited,
      },
      needsYou: row.needs_you ?? 0,
      blocking: row.blocking ?? 0,
      run: runFrom(row.run),
      threshold: thresholdFrom(row.threshold),
      copyOf: copyOfFrom(row.copy_of),
    })),
    total: api.total ?? api.items?.length ?? 0,
    page: api.page ?? 1,
    pageSize: api.page_size ?? 25,
    version: api.version,
  };
}

// ---------------------------------------------------------------------------
// The checklist
// ---------------------------------------------------------------------------

export type ChecklistStatus = 'open' | 'in_progress' | 'done' | 'not_possible' | 'waived';

/** In the order the picker offers them. */
export const CHECKLIST_STATUSES: readonly ChecklistStatus[] = [
  'open',
  'in_progress',
  'done',
  'not_possible',
  'waived',
];

/** Done, not possible or waived: nothing more to do on it. */
export function isSettled(status: ChecklistStatus): boolean {
  return status === 'done' || status === 'not_possible' || status === 'waived';
}

/** One line of the checklist: a check on a subject, or on the case. */
export interface ChecklistItem {
  id: string;
  /** `C1` … how the agents and the Assistant name it. */
  key: string;
  title: string;
  subjectId: string | null;
  status: ChecklistStatus;
  note: string | null;
  /** Who last set its status: an agent's name or a person's id. */
  changedBy: string | null;
  /** The person who added it; null for the agent's. */
  addedBy: string | null;
  position: number;
  supersededAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Every live record bearing on it, counted by how. */
  evidence: { supports: number; contradicts: number; context: number };
}

export interface ApiChecklistItem {
  id: string;
  key: string;
  title: string;
  subject_id: string | null;
  status: ChecklistStatus;
  note: string | null;
  changed_by: string | null;
  added_by: string | null;
  position: number;
  superseded_at: string | null;
  created_at: string;
  updated_at: string;
  evidence?: { supports: number; contradicts: number; context: number };
}

export function transformToChecklistItem(api: ApiChecklistItem): ChecklistItem {
  return {
    id: api.id,
    key: api.key,
    title: api.title,
    subjectId: api.subject_id,
    status: api.status,
    note: api.note,
    changedBy: api.changed_by,
    addedBy: api.added_by,
    position: api.position,
    supersededAt: api.superseded_at,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    evidence: api.evidence ?? { supports: 0, contradicts: 0, context: 0 },
  };
}

/** A line a person adds: on a subject, or (no subject) on the case. */
export interface ChecklistAddition {
  title: string;
  subjectId?: string | null;
  note?: string | null;
}

/** A status, a note, or both; nothing given changes nothing. */
export interface ChecklistChange {
  status?: ChecklistStatus;
  note?: string | null;
}

// ---------------------------------------------------------------------------
// The files
// ---------------------------------------------------------------------------

export type CaseFileKind =
  | 'client_file'
  | 'order_sheet'
  | 'partner_return'
  | 'provider_file'
  | 'partner_form'
  | 'other';

/** A file on the case: a client file or order sheet, a partner's return, a
 * provider's file — with the entry the model wrote for it, once it has. */
export interface WorkbenchFile {
  /** `F1` … once entered; null until the next run's or turn's prelude. */
  key: string | null;
  kind: CaseFileKind | null;
  filename: string;
  contentType: string | null;
  sizeBytes: number | null;
  /** How a tool names it: an attachment, or a `report_data` row. */
  conversationAttachmentId: string | null;
  reportDataId: string | null;
  /** The workbench's own file: the partner's form as the case agent
   * pre-filled it (kind `partner_form`), opened by this id, never entered. */
  prefillId: string | null;
  /** `description`, `outline`, `size`, `renderer` — what the model made of it. */
  entry: Record<string, unknown>;
  superseded: boolean;
  createdAt: string | null;
}

export interface ApiWorkbenchFile {
  key: string | null;
  kind: string | null;
  filename: string;
  content_type: string | null;
  size_bytes: number | null;
  source: Record<string, string>;
  entry: Record<string, unknown>;
  superseded: boolean;
  created_at: string | null;
}

const FILE_KINDS: readonly string[] = [
  'client_file',
  'order_sheet',
  'partner_return',
  'provider_file',
  'partner_form',
  'other',
];

export function transformToWorkbenchFile(api: ApiWorkbenchFile): WorkbenchFile {
  return {
    key: api.key,
    kind: api.kind && FILE_KINDS.includes(api.kind) ? (api.kind as CaseFileKind) : null,
    filename: api.filename,
    contentType: api.content_type,
    sizeBytes: api.size_bytes,
    conversationAttachmentId: api.source?.conversation_attachment_id ?? null,
    reportDataId: api.source?.report_data_id ?? null,
    prefillId: api.source?.prefill_id ?? null,
    entry: api.entry ?? {},
    superseded: api.superseded,
    createdAt: api.created_at,
  };
}

/** The entry's description, where the model has written one. */
export function fileDescription(file: WorkbenchFile): string | null {
  return textOf(file.entry.description);
}

/** POST /workbench/{id}/files: the list as it stands, and the rows the
 * upload made (a zip and its members), in order — an answer names the one
 * it uploaded. */
export interface WorkbenchUpload {
  files: WorkbenchFile[];
  version: string;
  uploaded: string[];
}

export interface ApiWorkbenchUpload {
  items: ApiWorkbenchFile[];
  version: string;
  uploaded: string[];
}

export function transformToWorkbenchUpload(api: ApiWorkbenchUpload): WorkbenchUpload {
  return {
    files: api.items.map(transformToWorkbenchFile),
    version: api.version,
    uploaded: api.uploaded,
  };
}

// ---------------------------------------------------------------------------
// The brief
// ---------------------------------------------------------------------------

/** How the case was asked for: an email's subject line and text. */
export interface BriefRequest {
  source: string | null;
  subjectLine: string | null;
  text: string | null;
  /** The text ran on past what's shown. */
  truncated: boolean;
}

interface ApiBriefRequest {
  source: string | null;
  subject_line: string | null;
  text: string | null;
  truncated: boolean;
}

function requestFrom(api: ApiBriefRequest | null | undefined): BriefRequest | null {
  return api
    ? {
        source: api.source,
        subjectLine: api.subject_line,
        text: api.text,
        truncated: api.truncated,
      }
    : null;
}

/** A subject, as someone picking the case up needs them. */
export interface BriefSubject {
  id: string;
  name: string;
  type: string | null;
  /** The client listed them without a name, and no one has given one yet. */
  unnamed: boolean;
  /** The case's own subject: the one most checklist items are on. */
  main: boolean;
  /** What identifies them ("label: value"). */
  identifiers: string[];
  /** Every person and company research found connected with them, with how
   * ("Ada Quill (manager)"), first found first. */
  ties: string[];
  /** Their checklist items by status. */
  checklist: Record<string, number>;
}

/** An open ask, as the brief lists it inline. */
export interface BriefAsk {
  id: string;
  kind: InterventionKind;
  section: Section;
  targetType: string | null;
  question: string | null;
  createdAt: string | null;
  /** A task waits on it. */
  blocking: boolean;
}

/** The last run's closing statement: what it did, what is outstanding,
 * what it would do next — and what it read. */
export interface BriefStatement {
  messageId: string;
  text: string;
  writtenAt: string | null;
  /** The playbook documents the run opened, and what the procedure asked
   * for that it did not. */
  reads: string[];
  missingReads: string[];
  /** `budget`: the run closed at the case's threshold. */
  halted: string | null;
  costUsd: string | null;
}

/** Where Start stands: whether the case has started, the request's stage,
 * and what Start still needs. */
export interface BriefStart {
  started: boolean;
  stage: string | null;
  needs: string[];
  canStart: boolean;
}

/** What a person may do from the brief. */
export interface BriefActions {
  canContinue: boolean;
  /** The live Word draft release would take, and the open errors it waits on. */
  releaseDraftId: string | null;
  releaseWaitsOn: string[] | null;
  canHandBack: boolean;
}

/** `GET /workbench/{rid}/brief`: the case for someone picking it up cold —
 * on the header's version. */
export interface WorkbenchBrief {
  reportId: string;
  codename: string | null;
  client: string | null;
  reportType: string | null;
  reportTypeName: string | null;
  dueAt: string | null;
  request: BriefRequest | null;
  subjects: BriefSubject[];
  checklist: ChecklistItem[];
  /** Every item by status. */
  checks: Record<string, number>;
  asks: BriefAsk[];
  /** What needs a person, by Needs you's section (the header's counts). */
  questions: Partial<Record<Section, number>>;
  statement: BriefStatement | null;
  spend: WorkbenchSpend;
  start: BriefStart;
  actions: BriefActions;
  run: WorkbenchRun;
  version: string;
}

export interface ApiWorkbenchBrief {
  report_id: string;
  codename?: string | null;
  client?: string | null;
  report_type?: string | null;
  report_type_name?: string | null;
  due_at?: string | null;
  request: ApiBriefRequest | null;
  subjects: {
    id: string;
    name: string;
    type: string | null;
    unnamed: boolean;
    main: boolean;
    identifiers: string[];
    ties: string[];
    checklist: Record<string, number>;
  }[];
  checklist?: ApiChecklistItem[];
  checks: Record<string, number>;
  asks?: {
    id: string;
    kind: InterventionKind;
    section: string;
    target_type: string | null;
    question: string | null;
    created_at: string | null;
    blocking: boolean;
  }[];
  questions: Record<string, number>;
  statement?: {
    message_id: string;
    text: string;
    written_at: string | null;
    reads: string[];
    missing_reads: string[];
    halted: string | null;
    cost_usd: string | null;
  } | null;
  spend: ApiWorkbenchSpend;
  start?: { started: boolean; stage: string | null; needs: string[]; can_start: boolean };
  actions?: {
    can_continue: boolean;
    release_draft_id: string | null;
    release_waits_on: string[] | null;
    can_hand_back: boolean;
  };
  run?: ApiWorkbenchRun;
  version: string;
}

export function transformToWorkbenchBrief(api: ApiWorkbenchBrief): WorkbenchBrief {
  return {
    reportId: api.report_id,
    codename: api.codename ?? null,
    client: api.client ?? null,
    reportType: api.report_type ?? null,
    reportTypeName: api.report_type_name ?? null,
    dueAt: api.due_at ?? null,
    request: requestFrom(api.request),
    subjects: api.subjects.map((s) => ({
      id: s.id,
      name: s.name,
      type: s.type,
      unnamed: s.unnamed,
      main: s.main,
      identifiers: s.identifiers,
      ties: s.ties,
      checklist: s.checklist ?? {},
    })),
    checklist: (api.checklist ?? []).map(transformToChecklistItem),
    checks: api.checks,
    asks: (api.asks ?? []).flatMap((a) =>
      isSection(a.section)
        ? [
            {
              id: a.id,
              kind: a.kind,
              section: a.section,
              targetType: a.target_type,
              question: a.question,
              createdAt: a.created_at,
              blocking: a.blocking,
            },
          ]
        : []
    ),
    questions: sectionCounts(api.questions),
    statement: api.statement
      ? {
          messageId: api.statement.message_id,
          text: api.statement.text,
          writtenAt: api.statement.written_at,
          reads: api.statement.reads ?? [],
          missingReads: api.statement.missing_reads ?? [],
          halted: api.statement.halted,
          costUsd: api.statement.cost_usd,
        }
      : null,
    spend: transformToWorkbenchSpend(api.spend),
    start: {
      started: api.start?.started ?? false,
      stage: api.start?.stage ?? null,
      needs: api.start?.needs ?? [],
      canStart: api.start?.can_start ?? false,
    },
    actions: {
      canContinue: api.actions?.can_continue ?? false,
      releaseDraftId: api.actions?.release_draft_id ?? null,
      releaseWaitsOn: api.actions?.release_waits_on ?? null,
      canHandBack: api.actions?.can_hand_back ?? false,
    },
    run: runFrom(api.run),
    version: api.version,
  };
}

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

export type EvidenceStatus =
  | 'claimed'
  | 'corroborated'
  | 'contradicted'
  | 'discounted'
  | 'unverifiable';

export type EvidenceRelation = 'supports' | 'contradicts' | 'context';

export interface EvidenceItem {
  id: string;
  /** Its number on the case: how every screen names it ("record 12"). */
  number: number;
  subjectId: string | null;
  kind: 'finding' | 'search';
  statement: string;
  quote: string | null;
  scope: Record<string, unknown> | null;
  sourceType: string;
  reportDataId: string | null;
  conversationAttachmentId: string | null;
  url: string | null;
  /** Where in its source: `{para_id}` in a Word file, `{page}` in a PDF. */
  locator: Record<string, unknown> | null;
  basis: string;
  status: EvidenceStatus;
  statusReason: string | null;
  decidedByType: string | null;
  decidedById: string | null;
  createdAt: string;
  /** The checklist items it bears on, and how. */
  links: { checklistItemId: string; relation: EvidenceRelation }[];
  /** A capture of the page research read (the "as seen" viewer opens it). */
  hasCapture: boolean;
  retrievedAt: string | null;
  /** Who it is about, as decided: `{verdict, basis, reason, by}` — the
   * identity resolver's or a person's. */
  identityBasis: Record<string, unknown> | null;
  /** A screening provider's item, when it came from one. */
  providerItemId: string | null;
  /** When a newer record replaced it. The list leaves these out; a record
   * opened by its id may be one. */
  supersededAt: string | null;
}

export interface ApiEvidenceItem {
  id: string;
  number: number;
  subject_id: string | null;
  kind: 'finding' | 'search';
  statement: string;
  quote: string | null;
  scope: Record<string, unknown> | null;
  source_type: string;
  report_data_id: string | null;
  conversation_attachment_id: string | null;
  url: string | null;
  locator: Record<string, unknown> | null;
  basis: string;
  status: EvidenceStatus;
  status_reason: string | null;
  decided_by_type: string | null;
  decided_by_id: string | null;
  created_at: string;
  links: { checklist_item_id: string; relation: EvidenceRelation }[];
  capture_s3_key?: string | null;
  retrieved_at?: string | null;
  identity_basis?: Record<string, unknown> | null;
  item_id?: string | null;
  superseded_at?: string | null;
}

export function transformToEvidenceItem(api: ApiEvidenceItem): EvidenceItem {
  return {
    id: api.id,
    number: api.number,
    subjectId: api.subject_id,
    kind: api.kind,
    statement: api.statement,
    quote: api.quote,
    scope: api.scope,
    sourceType: api.source_type,
    reportDataId: api.report_data_id,
    conversationAttachmentId: api.conversation_attachment_id,
    url: api.url,
    locator: api.locator,
    basis: api.basis,
    status: api.status,
    statusReason: api.status_reason,
    decidedByType: api.decided_by_type,
    decidedById: api.decided_by_id,
    createdAt: api.created_at,
    links: (api.links ?? []).map((l) => ({
      checklistItemId: l.checklist_item_id,
      relation: l.relation,
    })),
    hasCapture: Boolean(api.capture_s3_key),
    retrievedAt: api.retrieved_at ?? null,
    identityBasis: api.identity_basis ?? null,
    providerItemId: api.item_id ?? null,
    supersededAt: api.superseded_at ?? null,
  };
}

/** The Word paragraph an item was found in, if its locator names one. */
export function evidenceParaId(item: EvidenceItem): string | null {
  const paraId = item.locator?.para_id;
  return typeof paraId === 'string' && paraId ? paraId : null;
}

/**
 * The paragraph of the shown document an item was found in — only when the
 * document's paragraphs come from the very partner return the item came from
 * (its byte copy, or text edits of one, which keep every paragraph's id), so
 * the link is exact (not a template paragraph that happens to share an id).
 */
export function paragraphInDocument(
  item: EvidenceItem,
  document: Pick<WorkbenchHeader['document'], 'paragraphSourceReportDataId'>
): string | null {
  const paraId = evidenceParaId(item);
  const source = document.paragraphSourceReportDataId;
  return paraId && source && item.reportDataId === source ? paraId : null;
}

/** A person's edit to one paragraph's text (vNext-api `TextEdit`, sent as
 * is): a paragraph by its anchor, or a table's anchor and a cell; and the
 * text it replaces, so a stale edit is refused rather than misapplied. */
export interface DocumentTextEdit {
  anchor: { para_id: string | null; occurrence: number; pos: number | null };
  cell?: { row: number; col: number } | null;
  expected: string;
  text: string;
}

export interface EvidencePage {
  items: EvidenceItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** The most records one read of the case's evidence returns (the API's
 * page maximum). */
export const WORKBENCH_EVIDENCE_MAX_PAGE = 200;

/** What a read of the case's records asks for: a page, filtered. */
export interface EvidenceQuery {
  page: number;
  pageSize: number;
  status?: EvidenceStatus | null;
  subjectId?: string | null;
  checklistItemId?: string | null;
  includeSuperseded?: boolean;
}

/** One record as a screen names it — its number, and enough to say what it
 * is before it's opened whole (`GET /workbench/{rid}/evidence/index`). */
export interface EvidenceIndexEntry {
  id: string;
  number: number;
  subjectId: string | null;
  kind: 'finding' | 'search';
  sourceType: string;
  status: EvidenceStatus;
  /** Cut at 200 characters. */
  statement: string;
  superseded: boolean;
}

export interface ApiEvidenceIndex {
  items: {
    id: string;
    number: number;
    subject_id: string | null;
    kind: 'finding' | 'search';
    source_type: string;
    status: EvidenceStatus;
    statement: string;
    superseded?: boolean;
  }[];
  version: string;
}

export function transformToEvidenceIndex(api: ApiEvidenceIndex): EvidenceIndexEntry[] {
  return api.items.map((e) => ({
    id: e.id,
    number: e.number,
    subjectId: e.subject_id,
    kind: e.kind,
    sourceType: e.source_type,
    status: e.status,
    statement: e.statement,
    superseded: e.superseded ?? false,
  }));
}

// ---------------------------------------------------------------------------
// Who a record is about: what the identity check compared, and why not them
// ---------------------------------------------------------------------------

/** The identifiers the identity check compares, and the reasons a person
 * gives for "not them" — the same words. */
export type Identifier = 'name' | 'born' | 'country' | 'role' | 'other';
export type NotThemReason = 'born' | 'country' | 'role' | 'name' | 'other';

export const NOT_THEM_REASONS: { value: NotThemReason; label: string; key: string }[] = [
  { value: 'born', label: 'Born a different year', key: 'b' },
  { value: 'country', label: 'A different country', key: 'c' },
  { value: 'role', label: 'A different role', key: 'r' },
  { value: 'name', label: 'A different name', key: 'n' },
  { value: 'other', label: 'Something else', key: 'o' },
];

/** One identifier the identity check compared: the subject's, the
 * record's, and whether they agree (`silent`: the record doesn't say). */
export interface IdentifierCompared {
  identifier: Identifier;
  label: string;
  subject: string;
  record: string;
  agrees: 'match' | 'conflict' | 'silent';
}

/** The identifiers an identity ask or a record's `identity_basis` carries;
 * none where it was raised without them (its words say what was compared). */
export function identifiersOf(source: Record<string, unknown> | null): IdentifierCompared[] {
  const raw = source?.identifiers;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((entry): IdentifierCompared[] => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const e = entry as Record<string, unknown>;
    const agrees = e.agrees;
    if (agrees !== 'match' && agrees !== 'conflict' && agrees !== 'silent') {
      return [];
    }
    return [
      {
        identifier: (typeof e.identifier === 'string' ? e.identifier : 'other') as Identifier,
        label: typeof e.label === 'string' ? e.label : String(e.identifier ?? ''),
        subject: typeof e.subject === 'string' ? e.subject : '',
        record: typeof e.record === 'string' ? e.record : '',
        agrees,
      },
    ];
  });
}

// ---------------------------------------------------------------------------
// A hand search's sources
// ---------------------------------------------------------------------------

/** A source a person should search: its link where the ask gave one, or
 * how to reach it offline. */
export interface SourceToSearch {
  name: string;
  url: string | null;
  access: string | null;
}

/** A search ask's sources — plain names, or `{name, url, access}`. */
export function sourcesOf(payload: Record<string, unknown>): SourceToSearch[] {
  const raw = payload.sources;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((source): SourceToSearch[] => {
    if (typeof source === 'string') {
      const name = source.trim();
      if (!name) {
        return [];
      }
      return [{ name, url: /^https?:\/\//i.test(name) ? name : null, access: null }];
    }
    if (source && typeof source === 'object') {
      const s = source as Record<string, unknown>;
      const name = typeof s.name === 'string' ? s.name.trim() : '';
      if (!name) {
        return [];
      }
      const url = typeof s.url === 'string' && /^https?:\/\//i.test(s.url) ? s.url : null;
      const access = typeof s.access === 'string' && s.access.trim() ? s.access : null;
      return [{ name, url, access }];
    }
    return [];
  });
}

// ---------------------------------------------------------------------------
// Asks (interventions)
// ---------------------------------------------------------------------------

/** Two kinds of ask: a question — answered in words, by one of the options
 * offered, or in the shape its target takes — and an approval. */
export type InterventionKind = 'question' | 'approval';

export const INTERVENTION_LABELS: Record<InterventionKind, string> = {
  question: 'Question',
  approval: 'Approval',
};

export interface InterventionOption {
  id: string;
  label: string;
  /** Choosing it takes a typed answer ("Someone else": their name). */
  takesText?: boolean;
}

export interface Intervention {
  id: string;
  kind: InterventionKind;
  /** Where it sits in Needs you (from its target's type). */
  section: Section;
  /** `{type, subject_id?, checklist_item_id?, evidence_item_id?, jurisdiction?}`. */
  target: Record<string, unknown>;
  /** The question, its reason, options, the answer's form, a search's
   * sources and names, partner work's components. */
  payload: Record<string, unknown>;
  question: string | null;
  reason: string | null;
  options: InterventionOption[];
  /** The shape a question's answer takes: a search a person runs, an
   * identity verdict; null for words or a choice. */
  form: 'search' | 'identity' | null;
  waitingTaskId: string | null;
  status: 'open' | 'answered' | 'cancelled';
  answer: Record<string, unknown> | null;
  answeredBy: string | null;
  answeredAt: string | null;
  cancelReason: string | null;
  createdAt: string;
}

export interface ApiIntervention {
  id: string;
  kind: InterventionKind;
  section?: string | null;
  target: Record<string, unknown>;
  payload: Record<string, unknown>;
  waiting_task_id: string | null;
  status: 'open' | 'answered' | 'cancelled';
  answer: Record<string, unknown> | null;
  answered_by: string | null;
  answered_at: string | null;
  cancel_reason: string | null;
  created_at: string;
}

function optionsOf(payload: Record<string, unknown>): InterventionOption[] {
  const raw = payload.options;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((option): InterventionOption[] => {
    if (typeof option === 'string') {
      return [{ id: option, label: option }];
    }
    if (option && typeof option === 'object' && 'id' in option) {
      const { id, label, input } = option as { id: unknown; label?: unknown; input?: unknown };
      return [
        {
          id: String(id),
          label: typeof label === 'string' ? label : String(id),
          ...(input === 'text' ? { takesText: true } : {}),
        },
      ];
    }
    return [];
  });
}

export function transformToIntervention(api: ApiIntervention): Intervention {
  const payload = api.payload ?? {};
  const form = payload.form;
  return {
    id: api.id,
    kind: api.kind,
    section: isSection(api.section) ? api.section : 'other',
    target: api.target ?? {},
    payload,
    question: textOf(payload.question),
    reason: textOf(payload.reason),
    options: optionsOf(payload),
    form: form === 'search' || form === 'identity' ? form : null,
    waitingTaskId: api.waiting_task_id,
    status: api.status,
    answer: api.answer,
    answeredBy: api.answered_by,
    answeredAt: api.answered_at,
    cancelReason: api.cancel_reason,
    createdAt: api.created_at,
  };
}

/** What an ask is about, when its target names a checklist item or a record. */
export function interventionTarget(
  intervention: Intervention
): { type: 'checklist' | 'evidence'; id: string } | null {
  const target = intervention.target as {
    checklist_item_id?: unknown;
    evidence_item_id?: unknown;
  };
  if (typeof target.evidence_item_id === 'string') {
    return { type: 'evidence', id: target.evidence_item_id };
  }
  if (typeof target.checklist_item_id === 'string') {
    return { type: 'checklist', id: target.checklist_item_id };
  }
  return null;
}

/** The subject an ask is about, where its target names one. */
export function interventionSubjectId(intervention: Intervention): string | null {
  return textOf(intervention.target.subject_id);
}

/** A person's answer to "is this record about the subject?". */
export interface IdentityVerdict {
  /** `connected`: about one of the subject's ties, not the subject. */
  decision: 'same' | 'different' | 'connected' | 'uncertain';
  /** Why not them: with `different`, and only then. */
  reason?: NotThemReason;
}

/** A search a person ran by hand — a state register, a court docket — and
 * what came back (vNext-api `SearchCheck`). */
export interface SearchCheckAnswer {
  sources: string[];
  names: string[];
  result: 'none_found' | 'found';
  /** What was found; required when something was. */
  details?: string | null;
  /** YYYY-MM-DD. */
  searched_on?: string | null;
  /** What the search couldn't cover ("trading names only"). */
  limits?: string | null;
}

/** A question's answer: words, a choice among the options offered (with a
 * typed value where the choice takes one), the shape its form asks for — or
 * declined as not needed, with why in `note`. */
export interface QuestionAnswer {
  text?: string;
  choice?: string;
  value?: string;
  identity?: IdentityVerdict;
  search?: SearchCheckAnswer;
  conversation_attachment_id?: string;
  /** The question needs no answer; `note` says why. */
  not_needed?: true;
  note?: string;
}

/** An approval's answer; the budget raised carries the new threshold. */
export interface ApprovalAnswer {
  approved: boolean;
  budget_usd?: number;
  note?: string;
}

/** The answer shape each kind takes (`vnext/agents/interventions.py`),
 * sent as is. */
export type InterventionAnswer = QuestionAnswer | ApprovalAnswer;

// ---------------------------------------------------------------------------
// The timeline
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Conversations (plans/workbench2/06-chat-tab.md)
// ---------------------------------------------------------------------------

/** One of the case's conversations, in brief. */
export interface WorkbenchConversation {
  threadId: string;
  lastMessageAt: string | null;
  lastInboundAt: string | null;
  /** The last message that isn't a private note is the other side's. */
  awaitingReply: boolean;
  messages: number;
}

/** A partner and what they are to the case: in conversation, on the case
 * (locked on or shortlisted for an assignment), or one who could do the
 * work (a candidate of an open partner-work ask, with the record's terms
 * and history). */
export interface WorkbenchPartnerConversation {
  networkPartnerId: string;
  name: string | null;
  company: string | null;
  email: string | null;
  status: string;
  conversation: WorkbenchConversation | null;
  lockedOn: string[];
  shortlistedOn: string[];
  candidateFor: string[];
  sameWork: boolean;
  fee: string | null;
  currency: string | null;
  turnaroundDays: number | null;
  clientCases: number;
  otherCases: number;
}

/** An open partner-work ask, as an offer to a partner names the work. */
export interface WorkbenchOfferAsk {
  interventionId: string;
  subjectId: string | null;
  jurisdiction: string | null;
  components: string[];
  question: string | null;
}

/** GET /workbench/{id}/conversations: the client's conversation and the
 * partners', in conversation first, then on the case, then the candidates
 * best first; the open asks, for the offer. An evaluation copy has none. */
export interface WorkbenchConversations {
  evaluationCopy: boolean;
  client: WorkbenchConversation | null;
  partners: WorkbenchPartnerConversation[];
  asks: WorkbenchOfferAsk[];
  version: string;
}

interface ApiWorkbenchConversation {
  thread_id: string;
  last_message_at?: string | null;
  last_inbound_at?: string | null;
  awaiting_reply?: boolean;
  messages?: number;
}

export interface ApiWorkbenchConversations {
  report_id: string;
  evaluation_copy?: boolean;
  client?: ApiWorkbenchConversation | null;
  partners?: {
    network_partner_id: string;
    name?: string | null;
    company?: string | null;
    email?: string | null;
    status?: string;
    conversation?: ApiWorkbenchConversation | null;
    locked_on?: string[];
    shortlisted_on?: string[];
    candidate_for?: string[];
    same_work?: boolean;
    fee?: string | null;
    currency?: string | null;
    turnaround_days?: number | null;
    client_cases?: number;
    other_cases?: number;
  }[];
  asks?: {
    intervention_id: string;
    subject_id?: string | null;
    jurisdiction?: string | null;
    components?: string[];
    question?: string | null;
  }[];
  version: string;
}

function conversationFrom(api: ApiWorkbenchConversation | null | undefined) {
  return api
    ? {
        threadId: api.thread_id,
        lastMessageAt: api.last_message_at ?? null,
        lastInboundAt: api.last_inbound_at ?? null,
        awaitingReply: api.awaiting_reply ?? false,
        messages: api.messages ?? 0,
      }
    : null;
}

export function transformToWorkbenchConversations(
  api: ApiWorkbenchConversations
): WorkbenchConversations {
  return {
    evaluationCopy: api.evaluation_copy ?? false,
    client: conversationFrom(api.client),
    partners: (api.partners ?? []).map((p) => ({
      networkPartnerId: p.network_partner_id,
      name: p.name ?? null,
      company: p.company ?? null,
      email: p.email ?? null,
      status: p.status ?? 'unknown',
      conversation: conversationFrom(p.conversation),
      lockedOn: p.locked_on ?? [],
      shortlistedOn: p.shortlisted_on ?? [],
      candidateFor: p.candidate_for ?? [],
      sameWork: p.same_work ?? false,
      fee: p.fee ?? null,
      currency: p.currency ?? null,
      turnaroundDays: p.turnaround_days ?? null,
      clientCases: p.client_cases ?? 0,
      otherCases: p.other_cases ?? 0,
    })),
    asks: (api.asks ?? []).map((a) => ({
      interventionId: a.intervention_id,
      subjectId: a.subject_id ?? null,
      jurisdiction: a.jurisdiction ?? null,
      components: a.components ?? [],
      question: a.question ?? null,
    })),
    version: api.version,
  };
}

/** The file an ask was answered with, on that row: opened by its id. */
export interface ActivityFile {
  conversationAttachmentId: string;
  filename: string;
}

export interface WorkbenchActivityEvent extends CaseActivityEvent {
  source: 'classic' | 'workbench';
  /** What one agent run cost, on the row that ended it. */
  costUsd: string | null;
  file: ActivityFile | null;
}

export interface WorkbenchActivity {
  events: WorkbenchActivityEvent[];
  truncated: boolean;
}

export interface ApiWorkbenchActivity {
  report_id: string;
  truncated?: boolean;
  events: {
    id: string;
    occurred_at: string;
    kind: string;
    summary: string;
    detail?: string | null;
    actor_kind: CaseActivityEvent['actorKind'];
    actor_name?: string | null;
    actor_user_id?: string | null;
    entity_type?: string | null;
    entity_id?: string | null;
    entity_codename?: string | null;
    source: 'classic' | 'workbench';
    cost_usd?: string | null;
    file?: { conversation_attachment_id: string; filename: string } | null;
  }[];
}

export function transformToWorkbenchActivity(api: ApiWorkbenchActivity): WorkbenchActivity {
  return {
    truncated: api.truncated ?? false,
    events: api.events.map((e) => ({
      id: e.id,
      occurredAt: e.occurred_at,
      kind: e.kind,
      summary: e.summary,
      detail: e.detail ?? null,
      actorKind: e.actor_kind,
      actorName: e.actor_name ?? null,
      actorUserId: e.actor_user_id ?? null,
      entityType: e.entity_type ?? null,
      entityId: e.entity_id ?? null,
      entityCodename: e.entity_codename ?? null,
      source: e.source,
      costUsd: e.cost_usd ?? null,
      file: e.file
        ? { conversationAttachmentId: e.file.conversation_attachment_id, filename: e.file.filename }
        : null,
    })),
  };
}

/** POST /workbench/copies: the copy made. */
export interface WorkbenchCopyMade {
  reportId: string;
  reportRequestId: string;
  codename: string | null;
  familyKey: string | null;
  mode: WorkbenchMode;
  files: number;
  runs: number;
}

/** One of a copy's assignments, by what it covers. */
export interface WorkbenchReturnAssignment {
  id: string;
  jurisdictions: string[];
  components: string[];
  networkPartnerId: string | null;
  open: boolean;
}

/** A Word return on the case the copy was made from (no file name). */
export interface WorkbenchSourceReturn {
  reportDataId: string;
  npRequestId: number | null;
  receivedAt: string | null;
  superseded: boolean;
  pictures: number;
  sourceAssignmentId: string | null;
  jurisdictions: string[];
  components: string[];
  suggestedAssignmentId: string | null;
}

export interface WorkbenchSourceReturns {
  source: WorkbenchCopyOf;
  returns: WorkbenchSourceReturn[];
  assignments: WorkbenchReturnAssignment[];
}

export interface WorkbenchReturnTaken {
  reportDataId: string;
  assignmentId: string;
  takenFrom: string;
  files: number;
  runs: number;
}

export interface WorkbenchCopyRemoved {
  removed: Record<string, number>;
  filesDeleted: number;
}

export interface HandBackResult {
  fromMode: WorkbenchMode;
  cancelledTasks: number;
  closedInterventions: number;
  contentVersion: number | null;
}

// ---------------------------------------------------------------------------
// The documents: content versions, workbench drafts and partner returns
// ---------------------------------------------------------------------------

export type DocumentKind = 'version' | 'draft' | 'return';

export interface ReviewCounts {
  openErrors: number;
  openWarnings: number;
  closed: number;
  /** The open errors release waits on: the validators'. The reviewer's
   * judgement goes with the draft to QA. */
  holdingErrors: number;
}

/** One document the Document view's picker offers. */
export interface WorkbenchDocument {
  kind: DocumentKind;
  id: string;
  createdAt: string;
  filename: string;
  /** Word, which the viewer opens (a markdown-flow version isn't). */
  docx: boolean;
  /** A version's number, where it came from, and the draft it was released
   * from. */
  version: number | null;
  origin: string | null;
  fromDraftId: string | null;
  /** A draft's key in the case's workbench — what its review issues name. */
  workbenchKey: string | null;
  /** A draft's; a partner's return is `returned`, or `superseded` once
   * resubmitted. */
  status: 'built' | 'released' | 'superseded' | 'returned' | null;
  revision: boolean;
  releasedContentId: string | null;
  review: ReviewCounts | null;
  /** A draft a person made by editing one: who, and the paragraphs people
   * have changed since the reviewer read it (ids, upper-case; `pos:N`
   * without one). */
  author: string | null;
  changed: string[];
  /** The live Word draft only: the case's evidence or checklist changed
   * after its content was written. */
  outdated: boolean;
}

export interface ApiWorkbenchDocument {
  kind: DocumentKind;
  id: string;
  created_at: string;
  filename: string;
  docx: boolean;
  version: number | null;
  origin: string | null;
  from_draft_id: string | null;
  workbench_key: string | null;
  status: string | null;
  revision: boolean;
  released_content_id: string | null;
  review: {
    open_errors: number;
    open_warnings: number;
    closed: number;
    holding_errors?: number;
  } | null;
  author?: string | null;
  changed?: string[];
  outdated?: boolean;
}

const DOCUMENT_STATUSES: readonly string[] = ['built', 'released', 'superseded', 'returned'];

export function transformToWorkbenchDocument(api: ApiWorkbenchDocument): WorkbenchDocument {
  return {
    kind: api.kind,
    id: api.id,
    createdAt: api.created_at,
    filename: api.filename,
    docx: api.docx,
    version: api.version,
    origin: api.origin,
    fromDraftId: api.from_draft_id,
    workbenchKey: api.workbench_key,
    status:
      api.status && DOCUMENT_STATUSES.includes(api.status)
        ? (api.status as WorkbenchDocument['status'])
        : null,
    revision: api.revision,
    releasedContentId: api.released_content_id,
    review: api.review
      ? {
          openErrors: api.review.open_errors,
          openWarnings: api.review.open_warnings,
          closed: api.review.closed,
          holdingErrors: api.review.holding_errors ?? 0,
        }
      : null,
    author: api.author ?? null,
    changed: api.changed ?? [],
    outdated: api.outdated ?? false,
  };
}

/** The live Word draft: built, not yet released, Word. */
export function liveDraft(documents: WorkbenchDocument[]): WorkbenchDocument | null {
  return documents.find((d) => d.kind === 'draft' && d.status === 'built' && d.docx) ?? null;
}

// ---------------------------------------------------------------------------
// The partner's form (vNext-api docs/partner-form.md)
// ---------------------------------------------------------------------------

/** The case's own pre-fill (never leaves Hub), or one assignment's copy. */
export type PrefillScope = 'case' | 'assignment';
/** `built`: not on the partner's request (a refusal says why); `delivered`;
 * `superseded` by a newer build. */
export type PrefillStatus = 'built' | 'delivered' | 'superseded';

const PREFILL_STATUSES: readonly string[] = ['built', 'delivered', 'superseded'];

export function prefillStatus(status: string): PrefillStatus {
  return PREFILL_STATUSES.includes(status) ? (status as PrefillStatus) : 'built';
}

/** The partner's form as the workbench pre-filled it: the case's own, or
 * one assignment's copy — with its manifest (each table copy's checklist
 * items and filled cells). */
export interface WorkbenchPrefill {
  id: string;
  scope: PrefillScope;
  backofficeAssignmentId: string | null;
  npRequestId: number | null;
  filename: string;
  sha256: string;
  status: PrefillStatus;
  deliveredAt: string | null;
  deliveryError: string | null;
  supersededById: string | null;
  builtByTaskId: string | null;
  manifest: Record<string, unknown>;
  createdAt: string;
}

export interface ApiWorkbenchPrefill {
  id: string;
  scope: string;
  backoffice_assignment_id: string | null;
  np_request_id: number | null;
  filename: string;
  sha256: string;
  status: string;
  delivered_at: string | null;
  delivery_error: string | null;
  superseded_by_id: string | null;
  built_by_task_id: string | null;
  manifest: Record<string, unknown>;
  created_at: string;
}

export function transformToWorkbenchPrefill(api: ApiWorkbenchPrefill): WorkbenchPrefill {
  return {
    id: api.id,
    scope: api.scope === 'assignment' ? 'assignment' : 'case',
    backofficeAssignmentId: api.backoffice_assignment_id,
    npRequestId: api.np_request_id,
    filename: api.filename,
    sha256: api.sha256,
    status: prefillStatus(api.status),
    deliveredAt: api.delivered_at,
    deliveryError: api.delivery_error,
    supersededById: api.superseded_by_id,
    builtByTaskId: api.built_by_task_id,
    manifest: api.manifest ?? {},
    createdAt: api.created_at,
  };
}

/** The table copies a pre-fill holds, by its manifest. */
export function prefillCopies(prefill: WorkbenchPrefill): number {
  const copies = prefill.manifest.copies;
  return Array.isArray(copies) ? copies.length : 0;
}

/** What delivering one assignment's copy to its Backoffice request came
 * to: delivered (or delivered already), refused by Backoffice with why,
 * failed to reach it (Deliver tries again), or not cut from the case's
 * pre-fill (why). */
export interface PrefillDelivery {
  outcome: 'delivered' | 'repeated' | 'refused' | 'failed' | 'not_cut';
  prefillId: string | null;
  npRequestId: number | null;
  assignmentId: string | null;
  detail: string | null;
  actId: string | null;
}

export interface ApiPrefillDelivery {
  outcome: PrefillDelivery['outcome'];
  prefill_id?: string | null;
  np_request_id?: number | null;
  assignment_id?: string | null;
  detail?: string | null;
  act_id?: string | null;
}

export function transformToPrefillDelivery(api: ApiPrefillDelivery): PrefillDelivery {
  return {
    outcome: api.outcome,
    prefillId: api.prefill_id ?? null,
    npRequestId: api.np_request_id ?? null,
    assignmentId: api.assignment_id ?? null,
    detail: api.detail ?? null,
    actId: api.act_id ?? null,
  };
}

/** The copy reached the partner's request (now, or before). */
export function delivered(delivery: PrefillDelivery): boolean {
  return delivery.outcome === 'delivered' || delivery.outcome === 'repeated';
}

/** A rework round sent by a person: the return as it stands back onto the
 * partner's request, then the message on their thread — each once. */
export interface ReworkSent {
  /** `message_uncertain`: the document reached the request, the message's
   * fate is unknown (check the thread); `no_thread`: no thread to send it
   * on. */
  outcome: 'sent' | 'message_uncertain' | 'no_thread';
  interventionId: string;
  assignmentId: string;
  npRequestId: number;
  /** What went back: `return` (the partner's file) or `draft` (the adopted
   * draft of it). */
  document: string;
  message: string;
  documentActId: string | null;
  messageActId: string | null;
  detail: string | null;
}

export interface ApiReworkSent {
  outcome: ReworkSent['outcome'];
  intervention_id: string;
  assignment_id: string;
  np_request_id: number;
  document: string;
  message: string;
  document_act_id?: string | null;
  message_act_id?: string | null;
  detail?: string | null;
}

export function transformToReworkSent(api: ApiReworkSent): ReworkSent {
  return {
    outcome: api.outcome,
    interventionId: api.intervention_id,
    assignmentId: api.assignment_id,
    npRequestId: api.np_request_id,
    document: api.document,
    message: api.message,
    documentActId: api.document_act_id ?? null,
    messageActId: api.message_act_id ?? null,
    detail: api.detail ?? null,
  };
}

/** A rework ask: a partner's return sent back for correction — sent, or
 * declined, from Partner work; never approved blind. */
export function isRework(ask: Intervention): boolean {
  return ask.target.type === 'rework';
}

/** A partner's return adopted as the case's live Word draft. */
export interface AdoptedReturn {
  draftId: string;
  filename: string;
  sha256: string;
  reportDataId: string;
  /** The reviewer's open issues on the return, carried onto the draft. */
  issuesCarried: number;
}

export interface ApiAdoptedReturn {
  draft_id: string;
  filename: string;
  sha256: string;
  report_data_id: string;
  issues_carried?: number;
}

export function transformToAdoptedReturn(api: ApiAdoptedReturn): AdoptedReturn {
  return {
    draftId: api.draft_id,
    filename: api.filename,
    sha256: api.sha256,
    reportDataId: api.report_data_id,
    issuesCarried: api.issues_carried ?? 0,
  };
}

/** A Word draft's paragraphs, by id, and the evidence items each cites. */
export type DraftCitations = Record<string, string[]>;

// ---------------------------------------------------------------------------
// Review issues
// ---------------------------------------------------------------------------

export type ReviewIssueStatus = 'open' | 'fixed' | 'accepted' | 'dismissed';

/** A layout fault's one-step fix, made in the draft by code. */
export type LayoutFix = 'keep_with_next' | 'keep_row_together' | 'remove_blank_before';

/** What the reviewer found in a document, anchored where it is. */
export interface ReviewIssue {
  id: string;
  /** The workbench draft it's in, by key — or the partner's return, by id. */
  workbenchKey: string | null;
  reportDataId: string | null;
  source: 'validator' | 'layout' | 'judgement';
  rule: string;
  severity: 'error' | 'warning';
  fixable: boolean;
  message: string;
  /** The paragraph it's anchored on (a table's, with `row`), its position
   * without an id, the words quoted from it, and the page it's on. */
  paraId: string | null;
  pos: number | null;
  row: number | null;
  quote: string | null;
  page: number | null;
  /** A layout fault's one-step fix, where its anchor reaches it. */
  layoutFix: LayoutFix | null;
  status: ReviewIssueStatus;
  resolutionNote: string | null;
  createdAt: string;
}

export interface ApiReviewIssue {
  id: string;
  document: Record<string, unknown>;
  source: 'validator' | 'layout' | 'judgement';
  rule: string;
  severity: 'error' | 'warning';
  fixable: boolean;
  message: string;
  anchor: Record<string, unknown>;
  layout_fix?: LayoutFix | null;
  status: ReviewIssueStatus;
  resolution_note: string | null;
  created_at: string;
}

export function transformToReviewIssue(api: ApiReviewIssue): ReviewIssue {
  return {
    id: api.id,
    workbenchKey: textOf(api.document?.workbench_key),
    reportDataId: textOf(api.document?.report_data_id),
    source: api.source,
    rule: api.rule,
    severity: api.severity,
    fixable: api.fixable,
    message: api.message,
    paraId: textOf(api.anchor?.para_id),
    pos: countOf(api.anchor?.pos),
    row: countOf(api.anchor?.row),
    quote: textOf(api.anchor?.quote),
    page: countOf(api.anchor?.page),
    layoutFix: api.layout_fix ?? null,
    status: api.status,
    resolutionNote: api.resolution_note,
    createdAt: api.created_at,
  };
}

// ---------------------------------------------------------------------------
// Edits, release, the layout check, pages, a capture
// ---------------------------------------------------------------------------

/** A draft's changes saved: the draft they made, the issues its fixes
 * closed, and the paragraphs people have changed since the reviewer read
 * it. */
export interface DraftEdited {
  draftId: string;
  edits: number;
  fixed: string[];
  changed: string[];
}

export interface DraftRelease {
  draftId: string;
  contentId: string;
  version: number;
  /** Released before: nothing new was made. */
  repeated: boolean;
  /** The report's stage after it (`in_qa` where approval is required). */
  stage: string;
  backoffice: 'done' | 'waits_for_approval' | 'no_project' | 'failed';
}

export interface LayoutFinding {
  kind: string;
  page: number;
  message: string;
  severity: string;
  paraId: string | null;
  pos: number | null;
  row: number | null;
  /** Found without a model (`probe`) or by one (`look`). */
  source: 'probe' | 'look' | string;
  quote: string | null;
}

export interface LayoutCheck {
  pageCount: number;
  issues: LayoutFinding[];
  lookedAt: number[];
  /** The share of the document's words placed on a page. */
  placed: number;
  costUsd: string;
}

export interface ApiLayoutCheck {
  page_count: number;
  issues: {
    kind: string;
    page: number;
    message: string;
    severity: string;
    para_id: string | null;
    pos?: number | null;
    row?: number | null;
    source: string;
    quote: string | null;
  }[];
  looked_at: number[];
  placed: number;
  cost_usd: string;
}

export function transformToLayoutCheck(api: ApiLayoutCheck): LayoutCheck {
  return {
    pageCount: api.page_count,
    issues: api.issues.map((i) => ({
      kind: i.kind,
      page: i.page,
      message: i.message,
      severity: i.severity,
      paraId: i.para_id,
      pos: i.pos ?? null,
      row: i.row ?? null,
      source: i.source,
      quote: i.quote,
    })),
    lookedAt: api.looked_at,
    placed: api.placed,
    costUsd: api.cost_usd,
  };
}

/** A Word draft as the client gets it, page by page, and where each open
 * issue sits: its page and the box its paragraph or row fills there
 * (fractions of the page: left, top, right, bottom; none for a whole page). */
export interface DraftPages {
  pageCount: number;
  pages: { number: number; url: string; width: number; height: number }[];
  faults: {
    reviewIssueId: string;
    page: number;
    box: [number, number, number, number] | null;
  }[];
}

export interface ApiDraftPages {
  page_count: number;
  pages: { number: number; url: string; width: number; height: number }[];
  faults: {
    review_issue_id: string;
    page: number;
    box: [number, number, number, number] | null;
  }[];
}

export function transformToDraftPages(api: ApiDraftPages): DraftPages {
  return {
    pageCount: api.page_count,
    pages: api.pages,
    faults: api.faults.map((f) => ({ reviewIssueId: f.review_issue_id, page: f.page, box: f.box })),
  };
}

export interface CaptureLink {
  url: string;
  sourceUrl: string | null;
  retrievedAt: string | null;
}

// ---------------------------------------------------------------------------
// Setup (plans/workbench2, plan §0.6)
// ---------------------------------------------------------------------------

/** A subject on the case, as setup shows it. */
export interface SetupSubject {
  id: string;
  /** By the name the case knows them. */
  name: string;
  type: string | null;
  jurisdictions: string[];
  reference: string | null;
  /** What identifies them ("label: value"). */
  identifiers: string[];
  /** Listed without a name, and nobody has named them yet. */
  unnamed: boolean;
  /** Named by a person: what the client listed them as. */
  namedFrom: string | null;
  /** Today's identification: the candidate selected. */
  candidate: { id: string; name: string; confidence: string | null } | null;
}

/** The report type the case is drafted as, and what the playbook offers
 * this client. */
export interface SetupReportType {
  code: string | null;
  name: string | null;
  offered: { code: string; name: string | null }[];
}

/** A partner who could do the work, from today's records: who, and on
 * what terms they did it before. */
export interface SetupCandidate {
  networkPartnerId: string;
  name: string | null;
  /** Did one of the same components in the country; else other work there,
   * or (with no cases) only its scope lists the country. */
  sameWork: boolean;
  fee: string | null;
  /** Recorded on no past assignment: the case's own is shown. */
  currency: string | null;
  turnaroundDays: number | null;
  /** How often they did it: for this client, for any other. */
  clientCases: number;
  otherCases: number;
  lastAt: string | null;
  /** Active: may be locked in; else shortlisted only. */
  active: boolean;
  /** In conversation on the case: sounded out, from the records or not. */
  inConversation: boolean;
}

/** The case agent's partner work: an approval ask, with what came of it. */
/** A client file partner work names for the partner, by key. */
export interface SetupAskFile {
  key: string;
  filename: string | null;
  /** Still on the case (the key resolves). */
  onCase: boolean;
}

export interface SetupPartnerAsk {
  interventionId: string;
  status: string;
  subjectId: string | null;
  jurisdiction: string | null;
  /** The Backoffice components the work covers, as the ask names them. */
  components: string[];
  question: string | null;
  reason: string | null;
  /** Who could do it, best first. */
  candidates: SetupCandidate[];
  /** The client files the partner gets, as the ask names them; none named,
   * every client file as today. */
  files: SetupAskFile[];
  answer: Record<string, unknown> | null;
  /** The assignment it was approved into. */
  assignmentId: string | null;
  createdAt: string | null;
}

/** A partner assignment on the case. */
export interface SetupAssignment {
  id: string;
  status: string;
  outcome: string | null;
  networkPartnerId: string | null;
  partner: string | null;
  items: { component: string | null; subjectId: string | null; jurisdiction: string | null }[];
  fee: string | null;
  currency: string | null;
  dueDate: string | null;
  turnaroundDays: number | null;
  /** Locked partner, fee and a date: dispatch at Start takes it. */
  ready: boolean;
  /** Dispatched: the Backoffice request it is. */
  npRequestId: number | null;
  /** Why its last dispatch after Start was refused (Dispatch again retries). */
  syncError: string | null;
  /** The partner's form pre-filled for this assignment: cut from the case's
   * pre-fill at Start and put on the partner's request, or refused there
   * (with why); null until the case's pre-fill exists and the assignment is
   * dispatched. */
  prefill: SetupPrefill | null;
}

/** An assignment's copy of the partner's pre-filled form. */
export interface SetupPrefill {
  id: string;
  status: PrefillStatus;
  /** The table copies it holds. */
  copies: number;
  deliveredAt: string | null;
  /** Why Backoffice refused it (the partner has submitted, say). */
  deliveryError: string | null;
  createdAt: string | null;
}

export interface ApiSetupPrefill {
  id: string;
  status: string;
  copies?: number;
  delivered_at?: string | null;
  delivery_error?: string | null;
  created_at?: string | null;
}

export function transformToSetupPrefill(api: ApiSetupPrefill): SetupPrefill {
  return {
    id: api.id,
    status: prefillStatus(api.status),
    copies: api.copies ?? 0,
    deliveredAt: api.delivered_at ?? null,
    deliveryError: api.delivery_error ?? null,
    createdAt: api.created_at ?? null,
  };
}

/** The price set, and setup's suggestion — or why there is none. */
export interface SetupPrice {
  price: Money | null;
  turnaroundDays: number | null;
  suggested: Money | null;
  /** Where the suggestion comes from: `config`. */
  basis: string | null;
  unsuggested: string | null;
}

/** A value the case agent proposed for setup, waiting on the analyst:
 * applied through the Assistant's proposals. */
export interface SetupProposal {
  id: string;
  kind: string;
  summary: string;
  payload: Record<string, unknown>;
  messageId: string;
  createdAt: string | null;
}

/** Something Start still needs, or setup notices. `blocking`: today's gate
 * holds Start on it; else informational. */
export interface SetupNeed {
  code: string;
  label: string;
  blocking: boolean;
  /** Where to look: `subjects` · `report_type` · `price` · `partners` ·
   * `asks` · `files` · `config`. */
  target: string | null;
  count: number;
}

export interface SetupStart {
  started: boolean;
  stage: string | null;
  needs: SetupNeed[];
  canStart: boolean;
  /** The stages Start walks before the request starts. */
  remaining: string[];
}

export interface WorkbenchSetup {
  reportId: string;
  reportRequestId: string;
  mode: WorkbenchMode;
  /** The request's triage stage. */
  stage: TriageStage | string;
  started: boolean;
  /** A person sets it up here: live and not started. */
  editable: boolean;
  /** Partner work may still be approved, added or re-assigned: live and,
   * once started, not past partner work. */
  partnerWorkEditable: boolean;
  /** An evaluation copy: its partner work is never dispatched. */
  evaluationCopy: boolean;
  request: BriefRequest | null;
  files: WorkbenchFile[];
  /** The subjects are on the case once confirmed; before that, the case
   * agent's proposal lists them. */
  subjectsConfirmed: boolean;
  subjects: SetupSubject[];
  reportType: SetupReportType;
  checklist: ChecklistItem[];
  partners: {
    asks: SetupPartnerAsk[];
    assignments: SetupAssignment[];
    /** The case's own pre-fill of the partner's form, once built: each
     * partner's copy is cut from it at Start. */
    prefill: SetupPrefill | null;
  };
  price: SetupPrice;
  proposals: SetupProposal[];
  start: SetupStart;
  version: string;
}

export interface ApiWorkbenchSetup {
  report_id: string;
  report_request_id: string;
  mode: WorkbenchMode;
  stage: string;
  started: boolean;
  editable: boolean;
  partner_work_editable?: boolean;
  evaluation_copy?: boolean;
  request: ApiBriefRequest | null;
  files?: ApiWorkbenchFile[];
  subjects_confirmed: boolean;
  subjects: {
    id: string;
    name: string;
    type: string | null;
    jurisdictions: string[];
    reference: string | null;
    identifiers: string[];
    unnamed: boolean;
    named_from: string | null;
    candidate: { id: string; name: string; confidence: string | null } | null;
  }[];
  report_type: {
    code: string | null;
    name: string | null;
    offered: { code: string; name: string | null }[];
  };
  checklist?: ApiChecklistItem[];
  partners: {
    asks: {
      intervention_id: string;
      status: string;
      subject_id: string | null;
      jurisdiction: string | null;
      components: string[];
      question: string | null;
      reason: string | null;
      candidates: {
        network_partner_id: string;
        name: string | null;
        same_work: boolean;
        in_conversation?: boolean;
        fee: string | null;
        currency: string | null;
        turnaround_days: number | null;
        client_cases: number;
        other_cases: number;
        last_at: string | null;
        active?: boolean;
      }[];
      files?: { key: string; filename: string | null; on_case?: boolean }[];
      answer: Record<string, unknown> | null;
      assignment_id: string | null;
      created_at: string | null;
    }[];
    assignments: {
      id: string;
      status: string;
      outcome: string | null;
      np_request_id?: number | null;
      sync_error?: string | null;
      network_partner_id: string | null;
      partner: string | null;
      items: { component: string | null; subject_id: string | null; jurisdiction: string | null }[];
      fee: string | null;
      currency: string | null;
      due_date: string | null;
      turnaround_days: number | null;
      ready?: boolean;
      prefill?: ApiSetupPrefill | null;
    }[];
    prefill?: ApiSetupPrefill | null;
  };
  price: {
    price: { amount: string; currency: string | null } | null;
    turnaround_days?: number | null;
    suggested: { amount: string; currency: string | null } | null;
    basis: string | null;
    unsuggested: string | null;
  };
  proposals?: {
    id: string;
    kind: string;
    summary: string;
    payload: Record<string, unknown>;
    message_id: string;
    created_at: string | null;
  }[];
  start: {
    started: boolean;
    stage: string | null;
    needs: {
      code: string;
      label: string;
      blocking: boolean;
      target: string | null;
      count: number;
    }[];
    can_start: boolean;
    remaining?: string[];
  };
  version: string;
}

export function transformToWorkbenchSetup(api: ApiWorkbenchSetup): WorkbenchSetup {
  return {
    reportId: api.report_id,
    reportRequestId: api.report_request_id,
    mode: api.mode,
    stage: api.stage,
    started: api.started,
    editable: api.editable,
    partnerWorkEditable: api.partner_work_editable ?? false,
    evaluationCopy: api.evaluation_copy ?? false,
    request: requestFrom(api.request),
    files: (api.files ?? []).map(transformToWorkbenchFile),
    subjectsConfirmed: api.subjects_confirmed,
    subjects: api.subjects.map((s) => ({
      id: s.id,
      name: s.name,
      type: s.type,
      jurisdictions: s.jurisdictions ?? [],
      reference: s.reference,
      identifiers: s.identifiers ?? [],
      unnamed: s.unnamed,
      namedFrom: s.named_from,
      candidate: s.candidate,
    })),
    reportType: {
      code: api.report_type.code,
      name: api.report_type.name,
      offered: api.report_type.offered ?? [],
    },
    checklist: (api.checklist ?? []).map(transformToChecklistItem),
    partners: {
      asks: api.partners.asks.map((a) => ({
        interventionId: a.intervention_id,
        status: a.status,
        subjectId: a.subject_id,
        jurisdiction: a.jurisdiction,
        components: a.components ?? [],
        question: a.question,
        reason: a.reason,
        candidates: a.candidates.map((c) => ({
          networkPartnerId: c.network_partner_id,
          name: c.name,
          sameWork: c.same_work,
          fee: c.fee,
          currency: c.currency,
          turnaroundDays: c.turnaround_days,
          clientCases: c.client_cases,
          otherCases: c.other_cases,
          lastAt: c.last_at,
          active: c.active ?? true,
          inConversation: c.in_conversation ?? false,
        })),
        files: (a.files ?? []).map((f) => ({
          key: f.key,
          filename: f.filename ?? null,
          onCase: f.on_case ?? true,
        })),
        answer: a.answer,
        assignmentId: a.assignment_id,
        createdAt: a.created_at,
      })),
      assignments: api.partners.assignments.map((a) => ({
        id: a.id,
        status: a.status,
        outcome: a.outcome,
        npRequestId: a.np_request_id ?? null,
        syncError: a.sync_error ?? null,
        networkPartnerId: a.network_partner_id,
        partner: a.partner,
        items: a.items.map((i) => ({
          component: i.component,
          subjectId: i.subject_id,
          jurisdiction: i.jurisdiction,
        })),
        fee: a.fee,
        currency: a.currency,
        dueDate: a.due_date,
        turnaroundDays: a.turnaround_days,
        ready: a.ready ?? false,
        prefill: a.prefill ? transformToSetupPrefill(a.prefill) : null,
      })),
      prefill: api.partners.prefill ? transformToSetupPrefill(api.partners.prefill) : null,
    },
    price: {
      price: api.price.price,
      turnaroundDays: api.price.turnaround_days ?? null,
      suggested: api.price.suggested,
      basis: api.price.basis,
      unsuggested: api.price.unsuggested,
    },
    proposals: (api.proposals ?? []).map((p) => ({
      id: p.id,
      kind: p.kind,
      summary: p.summary,
      payload: p.payload ?? {},
      messageId: p.message_id,
      createdAt: p.created_at,
    })),
    start: {
      started: api.start.started,
      stage: api.start.stage,
      needs: api.start.needs.map((n) => ({
        code: n.code,
        label: n.label,
        blocking: n.blocking,
        target: n.target,
        count: n.count,
      })),
      canStart: api.start.can_start,
      remaining: api.start.remaining ?? [],
    },
    version: api.version,
  };
}

/** A subject to confirm onto the case, as the request listed them. */
export interface SetupSubjectInput {
  name: string;
  subjectType?: 'Person' | 'Company' | null;
  jurisdictions?: string[];
  reference?: string | null;
  identifiers?: Record<string, string>;
  role?: string | null;
  /** Where it was read: "the email", "order sheet tab 2". */
  where?: string | null;
}

/** A subject added after the case's were confirmed. */
export interface SetupSubjectAddition {
  name: string;
  subjectType?: 'Person' | 'Company' | null;
  reference?: string | null;
  jurisdictions?: string[];
  additionalInfo?: Record<string, unknown> | null;
}

/** Fields given are set; others stay. */
export interface SetupSubjectChange {
  name?: string;
  subjectType?: 'Person' | 'Company' | null;
  reference?: string | null;
  jurisdictions?: string[];
  additionalInfo?: Record<string, unknown> | null;
}

/** The commercials: a field present is set (null clears it); a turnaround
 * or a due date, not both. */
export interface SetupPriceChange {
  price?: string | null;
  currency?: string | null;
  turnaroundDays?: number | null;
  dueAt?: string | null;
  expedited?: boolean | null;
}

/** Partner work approved at setup: who, for how much, by when. */
export interface SetupPartnerApproval {
  networkPartnerId: string;
  npFee?: string | null;
  npCurrency?: string | null;
  /** One of the two, or neither. */
  npDueDate?: string | null;
  turnaroundTimeDays?: number | null;
  note?: string | null;
  /** The client files the partner gets, by key — overriding the ask's; null,
   * every client file as today. */
  files?: string[] | null;
}

/** The work moved to another partner: the request cancelled with the
 * reason, a new assignment locked to the partner at the terms (a term not
 * given keeps the old one), dispatched and delivered. */
export interface SetupReassign {
  networkPartnerId: string;
  reason: string;
  npFee?: string | null;
  npCurrency?: string | null;
  npDueDate?: string | null;
  turnaroundTimeDays?: number | null;
  note?: string | null;
}

/** A partner's terms moved after dispatch (today's assignment patch, pushed
 * onto the Backoffice request); a field given is set. */
export interface AssignmentTerms {
  npFee?: string | null;
  npCurrency?: string | null;
  npDueDate?: string | null;
  turnaroundTimeDays?: number | null;
  reason?: string | null;
}

/** Start pressed: the stages walked, and the request started. */
export interface SetupStarted {
  reportRequestId: string;
  walked: string[];
  stage: string;
  started: boolean;
  reportStage: string;
  /** Each dispatched assignment's copy of the partner's form, put on its
   * request by this click — or why not. */
  deliveries: PrefillDelivery[];
}
